import React, {useState} from 'react';
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {toast} from 'react-toastify';
import AdminMembershipsService from '../services/adminMembershipsService';
import SectionHeader from '@/shared/components/SectionHeader';
import {dollars} from '@/shared/utils/money';

function actionError(e: unknown): string {
    return (e as {data?: {error?: string}})?.data?.error ?? (e as Error)?.message ?? 'Action failed.';
}

const AdminMembershipsView: React.FC = () => {
    const [page, setPage] = useState(1);
    const {data, isLoading, isError} = useQuery({
        queryKey: ['admin', 'memberships', page],
        queryFn: () => AdminMembershipsService.list(page, 25, 'active'),
        staleTime: 30_000,
    });
    const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
    const qc = useQueryClient();
    const cancelMut = useMutation({
        mutationFn: (id: number) => AdminMembershipsService.cancel(id),
        onSuccess: () => {
            toast.success('Membership cancelled.');
            qc.invalidateQueries({queryKey: ['admin', 'memberships']});
        },
        onError: (e) => toast.error(actionError(e)),
    });
    const onCancel = (id: number) => {
        if (!window.confirm('Cancel this membership? Access continues through the end of the current period.')) return;
        cancelMut.mutate(id);
    };

    return (
        <div className="p-6 space-y-4">
            <SectionHeader title="My Labs Plus Memberships" />
            {isLoading && <p className="text-gray-500">Loading…</p>}
            {isError && <p className="text-mld-status-high">Could not load memberships.</p>}
            {data && (
                <>
                    <div className="overflow-x-auto bg-white rounded-2xl border border-mld-panel-border shadow-sm">
                        <table className="min-w-full text-sm">
                            <thead className="text-left text-gray-500 border-b border-mld-panel-border">
                                <tr>
                                    <th className="px-4 py-3">Customer</th>
                                    <th className="px-4 py-3">Plan</th>
                                    <th className="px-4 py-3">Status</th>
                                    <th className="px-4 py-3">Amount</th>
                                    <th className="px-4 py-3">Renews</th>
                                    <th className="px-4 py-3">Org</th>
                                    <th className="px-4 py-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.memberships.map((m) => (
                                    <tr key={m.id} className="border-b border-mld-panel-border">
                                        <td className="px-4 py-3 text-mld-text-primary">
                                            {m.customer_name ?? m.user_id}
                                            {m.customer_email && (
                                                <div className="text-gray-500">{m.customer_email}</div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 capitalize">{m.plan}</td>
                                        <td className="px-4 py-3 text-gray-500">
                                            {m.status}
                                            {m.cancel_at_period_end && (
                                                <span className="ml-1 text-mld-status-borderline">(cancelling)</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">{dollars(m.amount_cents)}</td>
                                        <td className="px-4 py-3">{m.next_charge_date ?? '—'}</td>
                                        <td className="px-4 py-3 text-gray-500">
                                            {m.organization_name ?? m.organization_id ?? '—'}
                                        </td>
                                        <td className="px-4 py-3">
                                            {!m.cancel_at_period_end && m.status === 'active' && (
                                                <button
                                                    type="button"
                                                    onClick={() => onCancel(m.id)}
                                                    className="rounded-md border border-gray-300 px-2 py-0.5 text-xs text-mld-status-high">
                                                    Cancel
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {data.memberships.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="px-4 py-6 text-center text-gray-500">
                                            No active memberships.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex items-center justify-end gap-2 text-sm text-gray-500">
                        <button
                            className="px-3 py-1 rounded-md border border-gray-300 disabled:opacity-40"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => p - 1)}>
                            Prev
                        </button>
                        <span>
                            Page {page} / {totalPages}
                        </span>
                        <button
                            className="px-3 py-1 rounded-md border border-gray-300 disabled:opacity-40"
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => p + 1)}>
                            Next
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default AdminMembershipsView;
